// src/modules/nlp/TextStatsLab.jsx
// Text Statistics Lab — Frequency · Zipf · Readability · Collocations · PMI · KWIC · Concordance
// Tabs: input | frequency | zipf | readability | collocations | kwic | compare | concepts

import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line, Legend, ScatterChart, Scatter, AreaChart, Area } from 'recharts';
import { tokenizeWords, tokenizeSentences, removeStopWords, tokenizeNgrams, textStats, computePMI, STOP_WORDS, SAMPLE_TEXTS } from '../../utils/nlpUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const ACCENT = 'var(--green)';
import { _livePalette as P } from '../../context/paletteSync';
const TABS = ['input','frequency','zipf','readability','collocations','kwic','compare','concepts'];

const Btn = ({onClick,children,color=ACCENT,disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Tag = ({children,color=ACCENT})=>(
  <span style={{padding:'2px 8px',background:color+'20',border:`1px solid ${color}44`,color,fontSize:'9px',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{children}</span>
);
const MC = ({label,value,sub,color=ACCENT})=>(
  <div style={{background:'var(--raised)',border:`1px solid ${color}22`,borderRadius:'3px',padding:'12px 14px'}}>
    <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>
    <div style={{fontSize:'18px',fontWeight:700,fontFamily:'Syne',color}}>{value}</div>
    {sub&&<div style={{fontSize:'10px',color:'var(--t3)',marginTop:'2px'}}>{sub}</div>}
  </div>
);

export default function TextStatsLab() {
  const [tab, setTab]         = useState('input');
  const [text, setText]       = useState(SAMPLE_TEXTS.tech);
  const [kwicWord, setKwicWord] = useState('');
  const [kwicWindow, setKwicWindow] = useState(5);
  const [topN, setTopN]       = useState(20);
  const [cmpText, setCmpText] = useState(SAMPLE_TEXTS.review);
  const [showRaw, setShowRaw] = useState(false);

  const stats   = textStats(text);
  const words   = tokenizeWords(text);
  const sents   = tokenizeSentences(text);

  // Full frequency map
  const allFreq = {};
  words.forEach(w=>{allFreq[w]=(allFreq[w]||0)+1;});
  const sortedAll = Object.entries(allFreq).sort((a,b)=>b[1]-a[1]);

  const contFreq = {};
  removeStopWords(words).filter(w=>w.length>2).forEach(w=>{contFreq[w]=(contFreq[w]||0)+1;});
  const sortedCont = Object.entries(contFreq).sort((a,b)=>b[1]-a[1]);

  // Zipf data
  const zipfData = sortedAll.slice(0,50).map(([word,count],rank)=>({
    rank:rank+1, word, count, expected:+(sortedAll[0][1]/(rank+1)).toFixed(0),
    logRank:+(Math.log10(rank+1)).toFixed(3), logFreq:+(Math.log10(count)).toFixed(3),
  }));

  // PMI collocations
  const pmiData = computePMI(text);

  // KWIC
  const kwicResults = (() => {
    if (!kwicWord.trim()) return [];
    const target = kwicWord.toLowerCase().trim();
    const results = [];
    const tokenArr = words;
    for (let i=0; i<tokenArr.length; i++) {
      if (tokenArr[i]===target||tokenArr[i].startsWith(target)) {
        const left  = tokenArr.slice(Math.max(0,i-kwicWindow),i).join(' ');
        const right = tokenArr.slice(i+1,i+1+kwicWindow).join(' ');
        results.push({left,target:tokenArr[i],right,pos:i});
      }
    }
    return results.slice(0,30);
  })();

  // Comparison stats
  const cmpStats  = textStats(cmpText);
  const cmpWords  = tokenizeWords(cmpText);
  const cmpFreq   = {};
  removeStopWords(cmpWords).filter(w=>w.length>2).forEach(w=>{cmpFreq[w]=(cmpFreq[w]||0)+1;});

  // Vocab overlap
  const vocab1 = new Set(Object.keys(contFreq));
  const vocab2 = new Set(Object.keys(cmpFreq));
  const shared = [...vocab1].filter(w=>vocab2.has(w));
  const jaccard = +(shared.length/(new Set([...vocab1,...vocab2]).size)).toFixed(4);

  // Char frequency for top text
  const charFreq={};
  text.toLowerCase().replace(/[^a-z]/g,'').split('').forEach(c=>{charFreq[c]=(charFreq[c]||0)+1;});
  const topChars = Object.entries(charFreq).sort((a,b)=>b[1]-a[1]).slice(0,10).map(([char,count])=>({char,count}));

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Header */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?ACCENT:'transparent'}`,background:tab===t?ACCENT+'18':'transparent',color:tab===t?ACCENT:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── INPUT ── */}
      {tab==='input' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="INPUT TEXT" accent={ACCENT}>
            <textarea value={text} onChange={e=>setText(e.target.value)} style={{width:'100%',minHeight:'160px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box'}}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px',flexWrap:'wrap'}}>
              {Object.entries(SAMPLE_TEXTS).map(([key])=>(
                <button key={key} onClick={()=>setText(SAMPLE_TEXTS[key])} style={{padding:'5px 12px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=ACCENT} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b1)'}>{key}</button>
              ))}
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            {[
              ['Words',stats.wordCount,ACCENT],['Unique',stats.uniqueWords,'var(--violet)'],
              ['Sentences',stats.sentences,'var(--gold)'],['Characters',stats.chars,'var(--orange)'],
              ['Avg word len',stats.avgWordLen+'c','var(--cyan)'],['Avg sent len',stats.avgSentLen+'w','var(--green)'],
              ['Lex diversity',stats.lexDiv,'var(--violet)'],['Stopwords',words.filter(w=>STOP_WORDS.has(w)).length,'var(--t3)'],
            ].map(([l,v,c])=>(
              <div key={l} style={{padding:'10px',background:'var(--raised)',border:`1px solid ${c}22`,borderRadius:'3px',textAlign:'center'}}>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px'}}>{String(l).toUpperCase()}</div>
                <div style={{fontSize:'20px',fontWeight:700,fontFamily:'Syne',color:c}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FREQUENCY ── */}
      {tab==='frequency' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="WORD FREQUENCY ANALYSIS" accent={ACCENT}>
            <div style={{display:'flex',gap:'10px',alignItems:'center',marginBottom:'12px',flexWrap:'wrap'}}>
              <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'11px',color:'var(--t2)',cursor:'pointer'}}>
                <input type="checkbox" checked={showRaw} onChange={e=>setShowRaw(e.target.checked)} style={{accentColor:ACCENT}}/>
                Include stopwords
              </label>
              <span style={{fontSize:'10px',color:'var(--t3)'}}>Top N:</span>
              {[10,20,30,50].map(n=>(
                <button key={n} onClick={()=>setTopN(n)} style={{padding:'4px 10px',border:`1px solid ${topN===n?ACCENT:'var(--b0)'}`,background:topN===n?ACCENT+'18':'var(--raised)',color:topN===n?ACCENT:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{n}</button>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={(showRaw?sortedAll:sortedCont).slice(0,topN).map(([word,count])=>({word,count}))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="word" stroke="var(--t3)" tick={{fontSize:8}} angle={-35} textAnchor="end" height={60} interval={0}/>
                <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                <Tooltip/>
                <Bar dataKey="count" radius={[2,2,0,0]} name="frequency">
                  {(showRaw?sortedAll:sortedCont).slice(0,topN).map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="CHARACTER FREQUENCY" accent="var(--violet)">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={topChars}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="char" stroke="var(--t3)" tick={{fontSize:11,fontFamily:'JetBrains Mono'}}/>
                  <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                  <Tooltip/>
                  <Bar dataKey="count" fill="var(--violet)" opacity={.8} radius={[2,2,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="WORD LENGTH DISTRIBUTION" accent="var(--gold)">
              {(()=>{
                const data=[1,2,3,4,5,6,7,8,9,10,12,15].slice(0,-1).map((lo,i,arr)=>({
                  len:`${lo}-${arr[i+1]||'∞'}`, count:words.filter(w=>w.length>=lo&&w.length<(arr[i+1]||999)).length
                }));
                return (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="len" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="count" fill="var(--gold)" opacity={.8} radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </Panel>
          </div>

          <Panel title="CUMULATIVE COVERAGE" accent={ACCENT}>
            {(()=>{
              const total=words.length;
              let cum=0;
              const data=sortedAll.slice(0,50).map(([word,count],i)=>{
                cum+=count;
                return {rank:i+1,word,coverage:+(cum/total*100).toFixed(1)};
              });
              return (
                <>
                  <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'10px'}}>
                    How many unique words are needed to cover X% of the text? Demonstrates Zipf's concentration.
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="rank" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'# unique words',position:'insideBottom',offset:-3,fill:'var(--t3)',fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={[0,100]} tickFormatter={v=>`${v}%`}/>
                      <Tooltip formatter={v=>[`${v}%`,'coverage']}/>
                      <Area type="monotone" dataKey="coverage" stroke={ACCENT} fill={ACCENT+'30'} strokeWidth={2}/>
                    </AreaChart>
                  </ResponsiveContainer>
                  <div style={{display:'flex',gap:'10px',marginTop:'10px',flexWrap:'wrap'}}>
                    {[10,50,90].map(pct=>{
                      const idx=data.findIndex(d=>d.coverage>=pct);
                      return idx>=0&&<Tag key={pct} color={ACCENT}>Top {idx+1} words = {pct}% coverage</Tag>;
                    })}
                  </div>
                </>
              );
            })()}
          </Panel>
        </div>
      )}

      {/* ── ZIPF ── */}
      {tab==='zipf' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ZIPF'S LAW — RANK vs FREQUENCY" accent="var(--violet)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              Zipf's law (1949): frequency ∝ 1/rank. The most frequent word appears ~2× more than the 2nd, ~3× more than the 3rd. Holds empirically across all natural languages and many other rank-frequency phenomena.
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={zipfData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="rank" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'rank',position:'insideBottom',offset:-3,fill:'var(--t3)',fontSize:9}}/>
                <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                <Tooltip formatter={(v,n,p)=>[v,p.dataKey==='count'?'actual freq':'Zipf prediction']}/>
                <Legend/>
                <Line type="monotone" dataKey="count"    stroke="var(--cyan)"   strokeWidth={2.5} dot={false} name="Actual freq"/>
                <Line type="monotone" dataKey="expected" stroke="var(--violet)" strokeWidth={1.5} dot={false} strokeDasharray="5 3" name="Zipf 1/rank"/>
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="LOG-LOG PLOT (Zipf linearizes to a straight line)" accent="var(--violet)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px'}}>
              In log-log space, Zipf's law becomes a straight line with slope −1: log(freq) = log(C) − log(rank). Deviations reveal morphological or stylistic properties of the text.
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="logRank" name="log rank" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'log₁₀(rank)',position:'insideBottom',offset:-3,fill:'var(--t3)',fontSize:9}} type="number" domain={['auto','auto']}/>
                <YAxis dataKey="logFreq" name="log freq" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'log₁₀(freq)',angle:-90,position:'insideLeft',fill:'var(--t3)',fontSize:9}} type="number" domain={['auto','auto']}/>
                <Tooltip cursor={{strokeDasharray:'3 3'}} content={({active,payload})=>active&&payload?.length?(
                  <div style={{background:'var(--raised)',border:'1px solid var(--b1)',padding:'8px',borderRadius:'3px',fontSize:'10px'}}>
                    <div style={{color:'var(--violet)',fontWeight:700}}>{payload[0]?.payload?.word}</div>
                    <div style={{color:'var(--t3)'}}>rank #{payload[0]?.payload?.rank}</div>
                    <div style={{color:'var(--t3)'}}>freq: {payload[0]?.payload?.count}</div>
                  </div>
                ):null}/>
                <Scatter data={zipfData} fill="var(--violet)" opacity={0.75} r={4}/>
              </ScatterChart>
            </ResponsiveContainer>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
            {[
              ['Zipf\'s Law (1949)','var(--violet)','George Kingsley Zipf found frequency rank × frequency ≈ constant. Applies to: word frequency, city sizes, income distribution, website traffic, earthquake magnitudes. A universal power law in complex systems.'],
              ['Heap\'s Law','var(--green)','Related: vocabulary size V(n) ∝ n^β where β≈0.4-0.6. As corpus grows, new words appear at a sublinear rate. Predicts: add 1M more words, how many new unique words. Used in search engine index sizing.'],
              ['Mandelbrot Generalization','var(--cyan)','Rank-frequency: f(r) = C/(r+b)^a where b,a are constants. Better fit than pure Zipf for very high and very low frequency words. Handles "hapax legomena" (words occurring once) better.'],
            ].map(([title,color,desc])=>(
              <div key={title} style={{padding:'13px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'11px',marginBottom:'7px'}}>{title}</div>
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.65}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── READABILITY ── */}
      {tab==='readability' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            <MC label="Flesch Reading Ease" value={stats.readability} sub={stats.readability>70?'Easy':stats.readability>50?'Standard':stats.readability>30?'Difficult':'Very difficult'} color={stats.readability>70?'var(--green)':stats.readability>50?'var(--gold)':stats.readability>30?'var(--orange)':'var(--red)'}/>
            <MC label="FK Grade Level"  value={stats.gradeLevel} sub={`Grade ${stats.gradeLevel} reading level`} color="var(--cyan)"/>
            <MC label="Avg Word Length" value={stats.avgWordLen+'c'} sub="chars per word" color="var(--violet)"/>
            <MC label="Avg Sent Length" value={stats.avgSentLen+'w'} sub="words per sentence" color="var(--gold)"/>
          </div>

          <Panel title="READABILITY FORMULAS COMPARED" accent="var(--gold)">
            {(()=>{
              const w=stats.wordCount, s=stats.sentences||1, sy=stats.syllables;
              const flesch     = +(206.835 - 1.015*(w/s) - 84.6*(sy/w)).toFixed(1);
              const fkGrade    = +(0.39*(w/s) + 11.8*(sy/w) - 15.59).toFixed(1);
              const gunning    = +(0.4*((w/s) + 100*(words.filter(w=>w.length>6).length/w))).toFixed(1);
              const ari        = +(4.71*(stats.chars/w) + 0.5*(w/s) - 21.43).toFixed(1);
              const colemanLiau= +(0.0588*(stats.chars/w*100) - 0.296*(s/w*100) - 15.8).toFixed(1);

              const metrics = [
                {name:'Flesch Ease',value:flesch,max:100,color:'var(--cyan)',note:'0-30 very hard, 60-70 standard, 90+ easy'},
                {name:'FK Grade',value:Math.min(20,Math.max(1,fkGrade)),max:20,color:'var(--green)',note:'US school grade level'},
                {name:'Gunning Fog',value:Math.min(20,Math.max(1,gunning)),max:20,color:'var(--gold)',note:'Years of education needed'},
                {name:'ARI',value:Math.min(20,Math.max(1,ari)),max:20,color:'var(--violet)',note:'Automated Readability Index'},
                {name:'Coleman-Liau',value:Math.min(20,Math.max(1,colemanLiau)),max:20,color:'var(--orange)',note:'Based on chars not syllables'},
              ];

              return (
                <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                  {metrics.map(({name,value,max,color,note})=>(
                    <div key={name}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                        <span style={{fontSize:'11px',color:'var(--t2)',fontWeight:600}}>{name}</span>
                        <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                          <span style={{fontSize:'10px',color:'var(--t3)'}}>{note}</span>
                          <span style={{fontSize:'13px',color,fontWeight:700,fontFamily:'JetBrains Mono',minWidth:'36px',textAlign:'right'}}>{value}</span>
                        </div>
                      </div>
                      <div style={{height:'6px',background:'var(--deep)',borderRadius:'3px'}}>
                        <div style={{height:'100%',width:`${(value/max)*100}%`,background:color,borderRadius:'3px',transition:'width .4s'}}/>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Panel>

          <Panel title="SENTENCE LENGTH DISTRIBUTION" accent="var(--green)">
            {(()=>{
              const sentLens=sents.map(s=>tokenizeWords(s).length);
              const bins=[0,5,10,15,20,25,30,40,50,100];
              const labels=bins.slice(0,-1).map((lo,i)=>`${lo+1}-${bins[i+1]}`);
              const data=labels.map((l,i)=>({l,count:sentLens.filter(n=>n>bins[i]&&n<=bins[i+1]).length}));
              return (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="l" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Bar dataKey="count" fill="var(--green)" opacity={.8} radius={[2,2,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            {[
              {grade:'1-3',ease:'90-100',desc:'Very simple. Dick and Jane readers. 5-6 word sentences. Mostly 1-syllable words. Primers and children\'s literature.'},
              {grade:'4-6',ease:'70-90',desc:'Simple. Children\'s books, comics, easy fiction. Sports articles. ~15 word sentences. Most readable adult text.'},
              {grade:'7-9',ease:'60-70',desc:'Standard. Teen fiction, popular magazines, consumer reports. ~18 word sentences. Target for mass communication.'},
              {grade:'10-12',ease:'50-60',desc:'Fairly difficult. Academic textbooks, literary fiction, technical manuals. ~20-25 word sentences. College-prep reading.'},
              {grade:'College',ease:'30-50',desc:'Difficult. Scholarly articles, professional journals, government documents. Long, complex sentences. Expert vocabulary.'},
              {grade:'Graduate',ease:'0-30',desc:'Very difficult. Academic papers, legal documents, philosophy. Dense, complex syntax. Technical jargon. Expert audience.'},
            ].map(({grade,ease,desc})=>(
              <div key={grade} style={{display:'flex',gap:'10px',padding:'10px',background:'var(--raised)',border:'1px solid var(--b0)',borderRadius:'3px',alignItems:'flex-start'}}>
                <div style={{flexShrink:0,textAlign:'center'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color:'var(--gold)',fontFamily:'JetBrains Mono'}}>{grade}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>ease: {ease}</div>
                </div>
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── COLLOCATIONS ── */}
      {tab==='collocations' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="COLLOCATIONS — POINTWISE MUTUAL INFORMATION (PMI)" accent="var(--orange)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              PMI(a,b) = log₂(P(a,b) / (P(a)×P(b))). High PMI = words co-occur much more than chance. Negative = avoid each other. 0 = independent. Identifies fixed expressions, technical terms, idioms.
            </div>
            {pmiData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={pmiData.slice(0,15)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                    <XAxis type="number" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'PMI score',position:'insideBottom',offset:-3,fill:'var(--t3)',fontSize:9}}/>
                    <YAxis type="category" dataKey="pair" stroke="var(--t3)" tick={{fontSize:9}} width={130}/>
                    <Tooltip formatter={v=>[v,'PMI']}/>
                    <Bar dataKey="pmi" radius={[0,2,2,0]}>
                      {pmiData.slice(0,15).map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{marginTop:'12px'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>ALL COLLOCATIONS TABLE</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'5px'}}>
                    {pmiData.map(({pair,count,pmi},i)=>(
                      <div key={pair} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:'var(--raised)',borderRadius:'2px',border:'1px solid var(--b0)'}}>
                        <span style={{fontSize:'11px',fontFamily:'JetBrains Mono',color:'var(--t1)'}}><span style={{color:'var(--t3)',marginRight:'4px'}}>#{i+1}</span>{pair}</span>
                        <div style={{display:'flex',gap:'8px'}}>
                          <span style={{fontSize:'10px',color:'var(--orange)',fontWeight:700}}>PMI:{pmi}</span>
                          <span style={{fontSize:'10px',color:'var(--t3)'}}>n:{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : <InfoBox color="var(--orange)" icon="◈">Load a longer text (300+ words) to detect collocations. Minimum 2 co-occurrences required.</InfoBox>}
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="BIGRAM FREQUENCY (raw)" accent="var(--gold)">
              {(()=>{
                const bg=tokenizeNgrams(removeStopWords(words).filter(w=>w.length>2),2);
                const bFreq={};bg.forEach(b=>{bFreq[b]=(bFreq[b]||0)+1;});
                const data=Object.entries(bFreq).sort((a,b)=>b[1]-a[1]).slice(0,12).map(([gram,count])=>({gram,count}));
                return (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="gram" stroke="var(--t3)" tick={{fontSize:8}} angle={-25} textAnchor="end" height={55} interval={0}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="count" fill="var(--gold)" opacity={.8} radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </Panel>

            <Panel title="COLLOCATION THEORY" accent="var(--orange)">
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                {[
                  ['PMI','log₂(P(x,y)/P(x)P(y))','Measures statistical independence. Unbounded, biased toward rare pairs. PPMI (Positive PMI) clips negatives to 0.'],
                  ['t-score','(f(x,y) - f(x)f(y)/N) / √f(x,y)','Null hypothesis testing for collocations. More reliable for frequent pairs than PMI. Standard in corpus linguistics.'],
                  ['Dice coefficient','2×f(x,y)/(f(x)+f(y))','Symmetric, range [0,1]. Used in term extraction. Less biased toward rare words than PMI. Good for balanced corpora.'],
                  ['χ² (chi-squared)','Σ(O-E)²/E','Tests if observed co-occurrence differs from expected. Handles zero counts better. Used in keyword extraction and term association.'],
                ].map(([name,formula,desc])=>(
                  <div key={name} style={{padding:'8px 10px',background:'var(--raised)',border:'1px solid var(--b0)',borderRadius:'3px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
                      <span style={{fontSize:'11px',fontWeight:700,color:'var(--orange)'}}>{name}</span>
                      <code style={{fontSize:'9px',color:'var(--code)',fontFamily:'JetBrains Mono'}}>{formula}</code>
                    </div>
                    <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.55}}>{desc}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ── KWIC ── */}
      {tab==='kwic' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="KWIC — KEY WORD IN CONTEXT (CONCORDANCE)" accent="var(--cyan)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              KWIC displays a search term in its surrounding context. Developed by Hans Peter Luhn (1960). Fundamental tool in corpus linguistics — reveals usage patterns, collocates, semantic prosody.
            </div>
            <div style={{display:'flex',gap:'10px',alignItems:'flex-end',marginBottom:'14px',flexWrap:'wrap'}}>
              <div style={{flex:1,minWidth:'200px'}}>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>SEARCH WORD</div>
                <input value={kwicWord} onChange={e=>setKwicWord(e.target.value)} placeholder="Type a word to search in context…" style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',boxSizing:'border-box'}}/>
              </div>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>CONTEXT WINDOW ±{kwicWindow}</div>
                <input type="range" min={2} max={12} value={kwicWindow} onChange={e=>setKwicWindow(+e.target.value)} style={{width:'120px',accentColor:'var(--cyan)'}}/>
              </div>
            </div>

            {kwicResults.length > 0 ? (
              <div>
                <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>{kwicResults.length} occurrences of "<span style={{color:'var(--cyan)'}}>{kwicWord}</span>"</div>
                <div style={{fontFamily:'JetBrains Mono',fontSize:'11px',background:'var(--deep)',border:'1px solid var(--b0)',borderRadius:'3px',overflow:'auto',maxHeight:'320px'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr style={{borderBottom:'1px solid var(--b0)'}}>
                      <th style={{padding:'6px 12px',color:'var(--t3)',fontSize:'9px',textAlign:'right',width:'40%'}}>LEFT CONTEXT</th>
                      <th style={{padding:'6px 12px',color:'var(--cyan)',fontSize:'9px',textAlign:'center',width:'20%'}}>NODE</th>
                      <th style={{padding:'6px 12px',color:'var(--t3)',fontSize:'9px',textAlign:'left',width:'40%'}}>RIGHT CONTEXT</th>
                    </tr></thead>
                    <tbody>
                      {kwicResults.map(({left,target,right},i)=>(
                        <tr key={i} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,217,255,0.03)'}}>
                          <td style={{padding:'5px 12px',color:'var(--t3)',textAlign:'right'}}>{left}</td>
                          <td style={{padding:'5px 12px',color:'var(--cyan)',fontWeight:700,textAlign:'center',background:'var(--gcyan)'}}>{target}</td>
                          <td style={{padding:'5px 12px',color:'var(--t3)',textAlign:'left'}}>{right}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{padding:'30px',textAlign:'center',color:'var(--t3)',fontSize:'11px'}}>
                {kwicWord ? `"${kwicWord}" not found in text` : 'Type a word above to see it in context'}
              </div>
            )}
          </Panel>

          <Panel title="SEMANTIC PROSODY — USAGE PATTERNS" accent="var(--cyan)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
              Semantic prosody: words acquire positive/negative associations from their typical co-texts even if the word itself is neutral. Example: "cause" almost always collocates with negative outcomes ("cause damage", "cause problems"). KWIC reveals these patterns.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {word:'cause',prosody:'NEGATIVE',example:'"cause damage","cause problems","cause concern"',color:'var(--red)'},
                {word:'happen',prosody:'NEGATIVE',example:'"what happened","accident happened","crisis happened"',color:'var(--orange)'},
                {word:'provide',prosody:'POSITIVE',example:'"provide support","provide solutions","provide access"',color:'var(--green)'},
                {word:'set in',prosody:'NEGATIVE',example:'"decay set in","panic set in","rot set in"',color:'var(--red)'},
                {word:'break out',prosody:'NEGATIVE',example:'"war broke out","fire broke out","riot broke out"',color:'var(--orange)'},
                {word:'commit',prosody:'NEGATIVE',example:'"commit crime","commit suicide","commit fraud"',color:'var(--red)'},
              ].map(({word,prosody,example,color})=>(
                <div key={word} style={{padding:'10px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'5px'}}>
                    <span style={{fontSize:'12px',fontWeight:700,color:'var(--t1)',fontFamily:'JetBrains Mono'}}>{word}</span>
                    <Tag color={color}>{prosody}</Tag>
                  </div>
                  <div style={{fontSize:'9px',color:'var(--t3)',fontStyle:'italic'}}>{example}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── COMPARE ── */}
      {tab==='compare' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="DOCUMENT COMPARISON" accent="var(--violet)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'12px'}}>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>DOCUMENT A (primary)</div>
                <textarea value={text} onChange={e=>setText(e.target.value)} style={{width:'100%',height:'100px',background:'var(--deep)',border:'1px solid var(--cyan)',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',boxSizing:'border-box'}}/>
              </div>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>DOCUMENT B (compare)</div>
                <textarea value={cmpText} onChange={e=>setCmpText(e.target.value)} style={{width:'100%',height:'100px',background:'var(--deep)',border:'1px solid var(--violet)',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',boxSizing:'border-box'}}/>
              </div>
            </div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {Object.entries(SAMPLE_TEXTS).map(([key])=>(
                <button key={key} onClick={()=>setCmpText(SAMPLE_TEXTS[key])} style={{padding:'4px 10px',border:'1px solid var(--b0)',background:'var(--raised)',color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{key} →B</button>
              ))}
            </div>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            {[
              ['Jaccard Similarity',jaccard,'var(--green)','vocab overlap'],
              ['Shared vocab',shared.length,'var(--cyan)','common words'],
              ['A unique',[...vocab1].filter(w=>!vocab2.has(w)).length,'var(--cyan)','only in A'],
              ['B unique',[...vocab2].filter(w=>!vocab1.has(w)).length,'var(--violet)','only in B'],
            ].map(([l,v,c,s])=>(
              <MC key={l} label={l} value={v} sub={s} color={c}/>
            ))}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="METRICS COMPARISON" accent="var(--violet)">
              {(()=>{
                const metrics=[
                  ['Words',stats.wordCount,cmpStats.wordCount,'var(--cyan)'],
                  ['Unique',stats.uniqueWords,cmpStats.uniqueWords,'var(--violet)'],
                  ['Sentences',stats.sentences,cmpStats.sentences,'var(--green)'],
                  ['Avg word len',stats.avgWordLen,cmpStats.avgWordLen,'var(--gold)'],
                  ['Avg sent len',stats.avgSentLen,cmpStats.avgSentLen,'var(--orange)'],
                  ['Lex diversity',stats.lexDiv,cmpStats.lexDiv,'var(--red)'],
                  ['Readability',stats.readability,cmpStats.readability,'var(--cyan)'],
                ];
                return (
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                    <thead><tr style={{borderBottom:'1px solid var(--b0)'}}>
                      <th style={{padding:'6px 10px',textAlign:'left',color:'var(--t3)',fontSize:'9px'}}>METRIC</th>
                      <th style={{padding:'6px 10px',textAlign:'right',color:'var(--cyan)',fontSize:'9px'}}>DOC A</th>
                      <th style={{padding:'6px 10px',textAlign:'right',color:'var(--violet)',fontSize:'9px'}}>DOC B</th>
                      <th style={{padding:'6px 10px',textAlign:'center',color:'var(--t3)',fontSize:'9px'}}>WINNER</th>
                    </tr></thead>
                    <tbody>
                      {metrics.map(([metric,a,b,c])=>(
                        <tr key={metric} style={{borderBottom:'1px solid var(--b0)'}}>
                          <td style={{padding:'7px 10px',color:'var(--t2)'}}>{metric}</td>
                          <td style={{padding:'7px 10px',color:'var(--cyan)',textAlign:'right',fontFamily:'JetBrains Mono',fontWeight:a>b?700:400}}>{a}</td>
                          <td style={{padding:'7px 10px',color:'var(--violet)',textAlign:'right',fontFamily:'JetBrains Mono',fontWeight:b>a?700:400}}>{b}</td>
                          <td style={{padding:'7px 10px',textAlign:'center'}}>{a===b?'—':a>b?<Tag color="var(--cyan)">A</Tag>:<Tag color="var(--violet)">B</Tag>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </Panel>

            <Panel title="VOCABULARY OVERLAP" accent="var(--violet)">
              <div style={{display:'flex',gap:'8px',marginBottom:'10px'}}>
                <div style={{flex:1,padding:'10px',background:'var(--gcyan)',border:'1px solid var(--cyan)44',borderRadius:'3px',textAlign:'center'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>A ONLY</div>
                  <div style={{fontSize:'16px',fontWeight:700,color:'var(--cyan)'}}>{[...vocab1].filter(w=>!vocab2.has(w)).length}</div>
                </div>
                <div style={{flex:1,padding:'10px',background:'rgba(176,138,255,.1)',border:'1px solid var(--violet)44',borderRadius:'3px',textAlign:'center'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>SHARED</div>
                  <div style={{fontSize:'16px',fontWeight:700,color:'var(--green)'}}>{shared.length}</div>
                </div>
                <div style={{flex:1,padding:'10px',background:'rgba(176,138,255,.1)',border:'1px solid var(--violet)44',borderRadius:'3px',textAlign:'center'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>B ONLY</div>
                  <div style={{fontSize:'16px',fontWeight:700,color:'var(--violet)'}}>{[...vocab2].filter(w=>!vocab1.has(w)).length}</div>
                </div>
              </div>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.08em'}}>WORDS UNIQUE TO A:</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'4px',maxHeight:'100px',overflowY:'auto'}}>
                {[...vocab1].filter(w=>!vocab2.has(w)).slice(0,30).map(w=>(
                  <span key={w} style={{padding:'2px 7px',background:'var(--gcyan)',border:'1px solid var(--cyan)33',color:'var(--cyan)',fontSize:'10px',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{w}</span>
                ))}
              </div>
              <div style={{fontSize:'10px',color:'var(--t3)',margin:'10px 0 6px',letterSpacing:'.08em'}}>WORDS UNIQUE TO B:</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'4px',maxHeight:'100px',overflowY:'auto'}}>
                {[...vocab2].filter(w=>!vocab1.has(w)).slice(0,30).map(w=>(
                  <span key={w} style={{padding:'2px 7px',background:'rgba(176,138,255,.1)',border:'1px solid var(--violet)33',color:'var(--violet)',fontSize:'10px',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{w}</span>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[
              {title:'Type-Token Ratio (TTR)',color:'var(--green)',desc:'TTR = unique_types / total_tokens. Measure of lexical richness. Range [0,1]. Higher = more varied vocabulary. Problem: TTR decreases as text gets longer! Solution: Measure of Textual Lexical Diversity (MTLD) uses moving windows to be length-independent.'},
              {title:'Hapax Legomena',color:'var(--cyan)',desc:'Words that appear exactly once. "Hapax" = Greek for "once only". Usually 40-60% of all unique words in a corpus are hapax legomena. They drive vocabulary growth (Heap\'s law) but contribute little to understanding. Important for smoothing in language models.'},
              {title:'Entropy & Information',color:'var(--violet)',desc:'Shannon entropy H(X) = -Σ p(x) log₂ p(x). Measures unpredictability of word distribution. Max entropy = uniform distribution (all words equally likely). Low entropy = predictable text. Text entropy ≈ 10-12 bits/word for natural language.'},
              {title:'Corpus Linguistics',color:'var(--gold)',desc:'Empirical study of language using large collections of real text. Key concepts: frequency, collocation, concordance, register, genre. Major corpora: British National Corpus (100M words), Google N-gram (1 trillion), Common Crawl (petabytes). Drives all NLP.'},
              {title:'Register & Style',color:'var(--orange)',desc:'Register: language variation by social situation (formal vs informal, technical vs casual). Style markers: avg sentence length, vocabulary richness, passive voice rate, Latinate vs Germanic word choice. Stylometry uses these features for authorship attribution.'},
              {title:'Text Complexity Research',color:'var(--red)',desc:'Coh-Metrix: 106 linguistic measures across lexical, syntactic, semantic, referential dimensions. Many readability formulas exist but Flesch-Kincaid is most widely used (legally required for some US government documents). BERT-based readability predictors now outperform formula-based methods.'},
            ].map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
